<template>
  <div>
    <!-- dialog visible during the page loading -->
    <loading-dialog :loading="loading" :loadingmsg="loadingmsg"></loading-dialog>

    <v-alert v-if="errored" v-show="showError" type="error" dark width="500px">
      <ul>
        <li v-for="msg in errorMessages" :key="msg">
          {{ msg }}
        </li>
      </ul>
    </v-alert>

    <v-row v-else dense justify="space-between">
      <v-col class="text-left">
        <locus-description-panel
          v-if="loadingInfo == false"
          :gene-id="geneId"
          :annotations="annotationData"
          :selected-genotypes="selectedGenotypes"
        ></locus-description-panel>
      </v-col>
      <v-col>
        <v-slider
          v-if="!loading && !errored"
          :value="currentMinDist"
          label="Divergence rate Threshold"
          :min="sliderMin"
          :max="sliderMax"
          :step="sliderStep"
          thumb-label="always"
          class="mt-10"
          @change="updateMindist"
        ></v-slider>
      </v-col>
      <v-col class="text-right">
        <v-row justify="end">
          <panel-button
            label="T"
            color="blue"
            title="Display Tree"
            :show="!displayTree"
            @click="showTree"
          ></panel-button>
          <panel-button
            label="A"
            color="#006400"
            title="Display Alignment"
            :show="!displayAln"
            @click="showAln"
          ></panel-button>
          <panel-button
            label="O"
            color="orange"
            title="Display Alignment Overview"
            :show="!displayOverview"
            @click="showOverview"
          ></panel-button>
          <panel-button
            label="I"
            color="brown"
            title="Display info"
            :show="!displayStats"
            @click="showStats"
          ></panel-button>
          <panel-button
            label="S"
            color="pink"
            title="Search"
            :show="!displaySearch"
            @click="showSearch"
          ></panel-button>
          <panel-button
            label="M"
            color="purple"
            title="Metadata"
            :show="!displayMetadata"
            @click="displayMetadata = true"
          ></panel-button>
          <panel-button
            label="?"
            color="#373434"
            title="Help"
            show
            @click="displayHelp = true"
          ></panel-button>
          <template v-if="!loading">
            <locus-link v-for="(l, i) in getLinksByLoci(geneId)" :key="i" :link="l"></locus-link>
            <v-btn class="mr-2" rounded color="primary" dark text small @click="reset">Reset</v-btn>
          </template>
        </v-row>
      </v-col>
    </v-row>
    <v-container v-if="!loading && !errored" fluid>
      <Help :show="displayHelp" :metadata="selectedMetadata" @hide="displayHelp = false"></Help>
      <v-row no-gutters>
        <v-col cols="12" :md="nbColsTreePanel">
          <v-row no-gutters> </v-row>
          <v-row no-gutters>
            <v-col v-if="displaySearch"
              ><v-card-gene-explore
                color="pink"
                title="Search"
                :parambuttonvisible="false"
                @show-parameters="showSearch = true"
                @hide="hideSearch"
              >
                <genotype-data-table
                  class="ma-2"
                  :genotypes="infoData.genotypes"
                  @select-genotypes="selectGenotypes"
                ></genotype-data-table></v-card-gene-explore
            ></v-col>
          </v-row>
          <v-row no-gutters>
            <v-col v-if="displayMetadata"
              ><v-card-gene-explore
                color="purple"
                title="Metadata"
                :parambuttonvisible="false"
                @hide="displayMetadata = false"
              >
                <metadata-table
                  class="ma-2"
                  :metadata="metadata.data"
                  :preselected="preselectedMetadata"
                  @select-metadata="selectMetadata"
                ></metadata-table></v-card-gene-explore
            ></v-col>
          </v-row>
          <v-row no-gutters>
            <v-col v-if="displayTree" ref="colTree" v-resize="resizeTree">
              <atlas-tree
                ref="tree"
                :height="heightTree"
                :width="widthTree"
                :tree="phylogram"
                :nodes-to-select="nodeToSelect"
                :highlighted-leave-ids="selectSeqIds"
                :collapsed="collapsedNodeIds"
                :glyphs="selectedMetadataToGlyph"
                @select-node="selectNode"
                @hide-tree="hideTree"
                @ready="treeReady"
              ></atlas-tree>
            </v-col>
          </v-row>
        </v-col>
        <v-col cols="12" :md="nbColsAlignmentPanel">
          <v-row justify="space-between" no-gutters>
            <v-col v-if="displayStats">
              <v-card-gene-explore
                color="brown"
                title="Info"
                :parambuttonvisible="false"
                @hide="hideStats"
              >
                <v-container class="infoContainer">
                  <stats :data="infoData"></stats>
                </v-container>
              </v-card-gene-explore>
            </v-col>
            <v-col v-if="displayOverview" ref="colOverview" v-resize="resizeOverview">
              <v-card-gene-explore
                color="orange"
                title="Alignment Overview"
                @show-parameters="showAlignmentOverviewParameters = true"
                @hide="hideOverview"
              >
                <v-container class="overviewContainer">
                  <overview-panel
                    :show-parameters="showAlignmentOverviewParameters"
                    :seqs="allSequences"
                    :selection="overviewSelection"
                    :width="widthOverview"
                    :metadata="selectedMetadataToMap"
                    :tracks="selectedTracks"
                    @hideParameters="showAlignmentOverviewParameters = false"
                  ></overview-panel>
                </v-container>
              </v-card-gene-explore>
            </v-col>
          </v-row>
          <!-- <br v-if="displayOverview || displayStats" /> -->
          <v-row no-gutters>
            <v-col v-if="displayAln">
              <v-card-gene-explore
                color="#006400"
                title="Alignment Details"
                @show-parameters="showAlignmentParameters = true"
                @hide="hideAln"
              >
                <alignment-panel
                  :max-pos="nbPositionsDisplayed"
                  :start="selectionFromOverview.startPos"
                  :end="selectionFromOverview.endPos"
                  :show-parameters="showAlignmentParameters"
                  :show-help="showAlignmentHelp"
                  :tracks="selectedTracks"
                  :tree="tree"
                  :metadata="selectedMetadataToMap"
                  :current-id="selectedNodeId"
                  :selected-ids="selectSeqIds"
                  :min-dist="currentMinDist"
                  :glyphs="selectedMetadataToGlyph"
                  :highlight-selection="selectSeqIds.length > 0 ? 'always' : 'never'"
                  @hideHelp="showAlignmentHelp = false"
                  @hideParameters="showAlignmentParameters = false"
                  @select-node="selectNode"
                  @extract="setSelectionFromAlignment"
                ></alignment-panel>
              </v-card-gene-explore>
            </v-col>
          </v-row>
        </v-col>
      </v-row>
    </v-container>
  </div>
</template>

<script>
import Vue from 'vue';

import SeqTree from '@/tree.js';
import SeqMetadata from '@/metadata.js';
import { loadJsonFile } from '@/axiosaccess.js';
import resize from 'vue-resize-directive';
import Stats from '@/components/stats/Stats.vue';
import OverviewPanel from '@/components/overview/OverviewPanel.vue';
import AtlasTree from '@/components/tree/AtlasTree.vue';
import AlignmentPanel from '@/components/alignment/AlignmentPanel.vue';
import GenotypeDataTable from '@/components/genotype/GenotypeDataTable.vue';
import VCardGeneExplore from '@/components/generic/VCardGeneExplore.vue';
import PanelButton from '@/components/generic/buttons/PanelButton.vue';
import LoadingDialog from '@/components/generic/LoadingDialog.vue';
import LocusDescriptionPanel from '@/components/locus/LocusDescriptionPanel.vue';
import LocusLink from '@/components/locus/LocusLink.vue';
import MetadataTable from '@/components/metadata/MetadataTable.vue';
import Help from '@/components/help/Help.vue';

import { mapGetters } from 'vuex';
import { mapActions } from 'vuex';
import { mapMutations } from 'vuex';

import { convertColor } from '@/common.js';

import _ from 'lodash';

const nbPositionsDisplayed = 160;
const defaultOpacity = 0.5;

export default {
  directives: {
    resize,
  },
  components: {
    AtlasTree,
    AlignmentPanel,
    Stats,
    OverviewPanel,
    LoadingDialog,
    PanelButton,
    VCardGeneExplore,
    LocusDescriptionPanel,
    LocusLink,
    GenotypeDataTable,
    MetadataTable,
    Help,
  },
  props: {
    species: {
      type: String,
      default: '',
    },
    release: {
      type: String,
      default: '',
    },
    geneId: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      tree: {},
      phylogram: {},
      heightTree: 400,
      metadata: {},
      selectedNodeId: '',
      /**
       * array of objects genotypes
       */
      selectedGenotypes: [],
      selectedTrackTypes: [],
      annotationData: [],
      infoData: {
        missings: [],
        distance_stats: {},
        genotypes: [],
        len_stats: {},
        alleles: [],
        locus_id: '',
        refseq_len: '',
        tracks: [],
      },

      widthTree: 600,
      widthOverview: 600,

      displayTree: true,
      displayAln: true,
      displayOverview: true,
      displayStats: false,
      displaySearch: false,
      displayMetadata: false,
      displayHelp: false,

      showAlignmentParameters: false,
      showAlignmentOverviewParameters: false,
      showAlignmentHelp: false,

      loadingTree: true,
      loadingMetadata: true,
      loadingInfo: true,
      loadingAnnotation: true,

      errored: false,
      errorMessages: [],

      loadingmsg: 'Loading...',

      nbPositionsDisplayed: nbPositionsDisplayed,
      overviewSelection: {
        startSeq: -1,
        endSeq: 100000000,
        startPos: 0,
        endPos: nbPositionsDisplayed,
      },
      selectionFromOverview: {
        startPos: 0,
        endPos: nbPositionsDisplayed,
      },

      selectedMetadata: [],

      preselectedMetadata: [],

      test: false,
      showError: false,
      sliderMin: '0.00',
      currentMinDist: 0,
      collapsedNodeIds: [],
    };
  },

  computed: {
    ...mapGetters(['getLinksByLoci', 'getUrlBase', 'getSpecies', 'getRelease', 'getErrorMsg']),
    loading: {
      get: function() {
        return (
          this.loadingTree || this.loadingMetadata || this.loadingInfo || this.loadingAnnotation
        );
      },
      set: function(val) {
        this.loadingTree = val;
        this.loadingMetadata = val;
        this.loadingInfo = val;
        this.loadingAnnotation = val;
      },
    },

    /**
     * Array of sequence objects corresponding to the leave of the tree
     */
    allSequences() {
      let allSequences = [];
      if (!_.isEmpty(this.tree)) {
        try {
          // allSequences = this.tree.getAllSequences().filter(s => s.isConsensus == false);
          allSequences = this.tree.getAllSequences();
        } catch (err) {
          this.displayError('Error while loading sequences');
          console.error(err.stack);
        }
      }

      return allSequences;
    },
    /**
     * list of seqids of the selected genotypes
     */
    selectSeqIds() {
      const selectedseqnames = this.selectedGenotypes.flatMap(g => g.seqids);
      return this.allSequences.filter(s => selectedseqnames.includes(s.name)).map(e => e.id);
    },
    selectedTracks() {
      let metadatasToConvert = this.selectedMetadata.filter(m => m.track);
      return this.convertMetadatasToTracks(metadatasToConvert);
    },
    selectedMetadataToMap() {
      return new SeqMetadata(this.selectedMetadata.filter(m => m.map));
    },
    selectedMetadataToGlyph() {
      let m = new SeqMetadata(this.selectedMetadata.filter(m => m.glyph));
      return m.formattedAsGlyph();
    },
    lengthSequence() {
      if (this.allSequences.length > 0) {
        return this.allSequences[0].seq.length;
      }
      return 0;
    },
    nbColsAlignmentPanel() {
      return this.displayTree || this.displaySearch ? 8 : 12;
    },
    nbColsTreePanel() {
      return this.displayAln || this.displayOverview || this.displayStats ? 4 : 12;
    },
    nodeToSelect() {
      return this.selectedNodeId != this.tree.getId() ? [this.selectedNodeId] : [];
    },
    urlInfoLocus() {
      return `${this.getUrlBase}${this.species}/${this.release}/${this.geneId}/locus`;
    },
    urlAlnTreeGene() {
      return `${this.getUrlBase}${this.species}/${this.release}/${this.geneId}/tree`;
    },
    urlAnnotationGene() {
      return `${this.getUrlBase}${this.species}/${this.release}/${this.geneId}/annotation`;
    },
    // A terme passer un genotype en parametre et le substituer a "reference"
    urlMetadataLocus() {
      return `${this.getUrlBase}${this.species}/${this.release}/${this.geneId}/alignmentMetadata/reference`;
    },
    /**
     * Step for the divergence rate slider
     */
    sliderStep() {
      return this.tree.getStepDefaultValue();
    },
    /**
     * return the maximum distance between node in the tree (equals to the distance of the root node)
     */
    sliderMax() {
      return this.tree.getMaxDivergenceRate() + this.sliderStep;
    },
  },
  watch: {
    lengthSequence(newValue) {
      let startPos = Math.floor((newValue - 1 - nbPositionsDisplayed / 2) / 2);
      let endPos = startPos + nbPositionsDisplayed;
      if (startPos >= 0 && endPos < newValue) {
        this.selectionFromOverview = Object.assign(
          this.selectionFromOverview,
          {},
          { startPos: startPos, endPos: endPos }
        );
        this.overviewSelection = Object.assign(
          this.overviewSelection,
          {},
          { startPos: startPos, endPos: endPos }
        );
      }
    },
    selectSeqIds(newValue) {
      this.tree.renameNodeConsensusFromList(newValue);
    },
  },
  created() {
    this.fetchListOfSpecies()
      .then(
        () => {
          if (!this.getSpecies(this.species)) {
            this.displayError('Unknown species ' + this.species);
          } else {
            this.setCurrentSpecies(this.species);
            // search the available releases
            this.fetchListOfReleases()
              .then(
                () => {
                  if (!this.getRelease(this.release)) {
                    this.displayError('Unknown Release with the identifier ' + this.release);
                  } else {
                    this.loadRelease(this.release).then(
                      () => {},
                      () => {
                        this.errored = true;
                        this.loading = false;
                      }
                    );
                  }
                },
                () => {
                  this.errored = true;
                  this.loading = false;
                }
              )
              .catch(() => {
                this.displayError('Error during loading releases');
                this.loading = false;
              });
          }
        },
        () => {
          this.errored = true;
          this.loading = false;
        }
      )
      .catch(() => {
        this.displayError('Error during loading species');
        this.loading = false;
      });

    this.loadLocus();
  },
  methods: {
    ...mapActions(['loadRelease', 'fetchListOfSpecies', 'fetchListOfReleases', 'fetchListOfGenes']),
    ...mapMutations(['setCurrentSpecies']),
    /**
     * Call when the user change the root node to display
     *  change the selectednode id
     *  compute the overviewSelection positions
     */
    selectNode(node) {
      if (node.length > 0) {
        this.selectedNodeId = node[0];
      } else {
        this.selectedNodeId = this.tree.getId();
      }

      let a_indices = [];
      if (this.selectedNodeId != '') {
        let a_selectedSequences = this.tree.getSequencesWithMinDist(this.selectedNodeId, 0, 0);

        for (let index = 0; index < this.allSequences.length; index++) {
          const seq = this.allSequences[index];
          if (a_selectedSequences.some(s => s.id === seq.id)) {
            a_indices.push(index);
          }
        }
      }
      let min = Math.min(...a_indices);
      let max = Math.max(...a_indices);
      this.overviewSelection = Object.assign({}, this.overviewSelection, {
        startSeq: min,
        endSeq: max,
      });

      Vue.nextTick();
    },
    /**
     * Reset all the views
     */
    reset() {
      this.loadLocus();
      this.displayOverview = true;
      this.displayAln = true;
      this.displayTree = true;
      this.displayStats = false;
      this.displayMetadata = false;
      this.selectedGenotypes = [];
    },
    /**
     * load all data useful to display a locus
     */
    loadLocus() {
      console.log('Load locus');
      this.loading = true;
      this.resetOverviewSelection();
      this.resetSelectionFromOverview();

      this.loadTree();

      this.loadLocusInformation();

      this.loadAnnotation();
    },

    /**
     * Load the json containing the tree
     */
    loadTree() {
      this.loadingTree = true;
      const baseMessage = 'Error when loading tree';
      loadJsonFile(this.urlAlnTreeGene, 'locus tree')
        .then(
          resp => {
            this.tree = new SeqTree(resp.data);
            this.phylogram = JSON.stringify(resp.data, (key, value) => {
              if (key === 'sequence') {
                return undefined;
              } else {
                return value;
              }
            });

            this.selectedNodeId = this.tree.getId();
            this.loadingTree = false;
            this.currentMinDist = this.tree.getDistDefaultValue();
            this.collapsedNodeIds = this.tree.getCollapsedNodeIds(this.currentMinDist);
            // Need to load metadata after the tree is loaded
            this.loadMetadata();
          },
          err => {
            let message = err.response.data.message
              ? baseMessage + ': ' + err.response.data.message
              : baseMessage;
            this.displayError(message);
            this.loadingTree = false;
          }
        )
        .catch(() => {
          this.displayError(baseMessage);
          this.loadingTree = false;
        });
    },

    /**
     * Load the json containing the metadata
     */
    loadMetadata() {
      this.loadingMetadata = true;
      const baseMessage = 'Error when loading locus metadata';

      loadJsonFile(this.urlMetadataLocus, 'Locus metadata')
        .then(
          resp => {
            let convertedData = this.convertMetadata(resp.data);

            console.log('convertedData', convertedData.length);
            this.metadata = new SeqMetadata(convertedData);
            this.preselectedMetadata = this.metadata.getMetadataByType([
              'genericPositions',
              'conservation',
            ]);

            this.selectedMetadata = this.metadata.getMetadataByType([
              'genericPositions',
              'conservation',
            ]);
            this.loadingMetadata = false;
          },
          err => {
            let message = err.response.data.message
              ? baseMessage + ': ' + err.response.data.message
              : baseMessage;
            this.displayError(message);
            this.loadingMetadata = false;
          }
        )
        .catch(() => {
          this.displayError(baseMessage);
          this.loadingMetadata = false;
        });
    },

    /**
     * Load the json containing the locus information
     */
    loadLocusInformation() {
      this.loadingInfo = true;
      const baseMessage = 'Error when loading locus information';
      loadJsonFile(this.urlInfoLocus, 'locus information')
        .then(
          resp => {
            this.infoData = resp.data;
            this.loadingInfo = false;
          },
          err => {
            let message = err.response.data.message
              ? baseMessage + ': ' + err.response.data.message
              : baseMessage;
            this.displayError(message);
            this.loadingInfo = false;
          }
        )
        .catch(() => {
          this.displayError(baseMessage);
          this.loadingInfo = false;
        });
    },

    /**
     * Load the json corresponding to the annotation
     */
    loadAnnotation() {
      this.loadingAnnotation = true;
      const baseMessage = 'Error when loading gene annotation';
      loadJsonFile(this.urlAnnotationGene, 'functional annotation')
        .then(
          resp => {
            this.annotationData = resp.data;
            this.loadingAnnotation = false;
          },
          err => {
            let message = err.response.data.message
              ? baseMessage + ': ' + err.response.data.message
              : baseMessage;
            this.displayError(message);
            this.loadingAnnotation = false;
          }
        )
        .catch(() => {
          this.displayError('Error during loading gene annotation');
          this.loadingAnnotation = false;
        });
    },

    resizeTree() {
      if (this.loadingTree == false) {
        this.widthTree = 'colTree' in this.$refs ? this.$refs.colTree.clientWidth - 10 : 600;
      }
    },
    resizeOverview() {
      if (this.loadingTree == false) {
        this.widthOverview =
          'colOverview' in this.$refs ? this.$refs.colOverview.clientWidth - 50 : 600;
      }
    },
    showTree() {
      this.displayTree = true;
    },
    hideTree() {
      this.displayTree = false;
    },
    showAln() {
      this.displayAln = true;
    },
    hideAln() {
      this.displayAln = false;
    },
    hideSearch() {
      this.displaySearch = false;
    },
    showOverview() {
      this.displayOverview = true;
    },
    hideOverview() {
      this.displayOverview = false;
    },
    showStats() {
      this.displayStats = true;
    },
    showSearch() {
      this.displaySearch = true;
    },
    hideStats() {
      this.displayStats = false;
    },
    /**
     * Methods used by msa overview
     */
    resetOverviewSelection() {
      this.overviewSelection = {
        startSeq: -1,
        endSeq: 100000000,
        startPos: 0,
        endPos: nbPositionsDisplayed,
      };
    },
    resetSelectionFromOverview() {
      this.selectionFromOverview = {
        startPos: 0,
        endPos: nbPositionsDisplayed,
      };
    },
    setSelectionFromAlignment(pos) {
      this.overviewSelection = Object.assign({}, this.overviewSelection, {
        startPos: pos[0],
        endPos: pos[1],
      });
    },
    selectGenotypes(selectedgenotypes) {
      this.selectedGenotypes = selectedgenotypes;
    },
    displayError(msg) {
      this.showError = true;
      this.errored = true;
      this.errorMessages.push(msg);
    },
    selectMetadata(selected) {
      this.selectedMetadata = selected;
    },
    /**
     * Converts a metadata to a track
     * TODO : put in metadata.js ?
     * @param {metadata} m
     */
    convertMetadataToTrack(m) {
      let track = {
        trackLabel: m.label,
        features: [],
      };
      m.categories.forEach(c => {
        c.regions.forEach(r =>
          track.features.push({
            positions: r.ranges,
            type: '',
            color: c.style.fill,
            'fill-opacity': c.style['fill-opacity'],
          })
        );
      });
      return track;
    },

    /**
     * Converts a metadata to a track
      TODO : put in metadata.js ?
     * @param {metadata} m
     */
    convertMetadatasToTracks(metadatas) {
      return metadatas.map(m => {
        return this.convertMetadataToTrack(m);
      });
    },

    /**
     * Convert names to ids in the data
     */
    convertMetadata(data) {
      return data.map((m, idx) => {
        let map = false;
        let disableMap = true;
        let track = false;
        let disableTrack = true;
        let glyph = false;
        let displayModes = [];
        if (m.displayModes) {
          map = m.displayModes.includes('map');
          disableMap = !m.displayModes.includes('map');
          track = m.displayModes.includes('track');
          disableTrack = !m.displayModes.includes('track');
          glyph = m.displayModes.includes('genotype');
          if (map && track) {
            map = false;
          }
          displayModes = m.displayModes;
        }
        let label = m.label;
        let trackType = m.trackType;
        let categories = m.categories.map(cat => {
          let style = { fill: 'black', 'fill-opacity': defaultOpacity };
          if ('style' in cat) {
            if ('fill' in cat.style) {
              if (cat.style.fill.startsWith('#')) {
                style.fill = cat.style.fill;
              } else {
                style.fill = convertColor(cat.style.fill);
              }
            }
            if ('fill-opacity' in cat.style) {
              style['fill-opacity'] = cat.style['fill-opacity'];
            }
          }
          cat.style = Object.assign({}, cat.style, style);

          if (trackType !== 'conservation') {
            if (cat.hasOwnProperty('regions')) {
              let regions = cat.regions.map(r => {
                let seq = this.allSequences.find(s => s.name === r.id);

                if (seq) {
                  r.id = seq.id;
                }
                return r;
              });
              cat.regions = regions;
            }
          }

          return cat;
        });

        return {
          label,
          trackType,
          map,
          track,
          disableMap,
          disableTrack,
          categories,
          displayModes,
          idx,
          glyph,
        };
      });
    },
    updateMindist(v) {
      this.currentMinDist = v;
      this.collapsedNodeIds = this.tree.getCollapsedNodeIds(this.currentMinDist);
    },
    treeReady(o) {
      this.heightTree = o.nbLeaves * 3 + 100;
    },
  },
  /**
   * Generic behaviour if an error is captured
   */
  errorCaptured(err) {
    this.errored = true;
    this.errorMessages.push('Error : data is maybe badly formatted, please contact Atlas admin');
    console.error('Captured error : ', err.stack);
    return false;
  },
};
</script>

<style scoped>
.overviewContainer {
  height: 330px;
  min-width: 650px;
  margin-left: 0;
  margin-right: 0;
  margin-top: 0px;
}

.infoContainer {
  height: 350px;
  overflow: auto;
}

.v-card {
  margin: 5px;
}

.v-alert {
  margin: 100px auto;
}
</style>
